import React, { useState, useEffect, useRef } from "react";
import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { FaEdit } from "react-icons/fa"; // Import the Edit icon
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosinstance";
import "./Table.css";
import Pagination from "../Components/Pagination";
import { Edit } from "lucide-react";
import { PiSortAscendingThin } from "react-icons/pi";
import {
  formatBadgeLabel,
  getVisitorTypeBadgeClass,
} from "../helperFunctions";

function SystemUsers({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const containerRef = useRef();

  const [users, setUsers] = useState([]); // State to hold user data
  const [loading, setLoading] = useState(true); // State for loading
  const [error, setError] = useState(null); // State for error handling
  const [searchVisible, setSearchVisible] = useState(false); // State for toggling search bar
  const [searchQuery, setSearchQuery] = useState(""); // State to hold search query
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalRecords, settotalRecords] = useState();
  const [savingIds, setSavingIds] = useState(new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const rangeBtnRef = useRef(null);
  const rangePopupRef = useRef(null);

  const isValidRange =
    !startDate || !endDate || new Date(startDate) <= new Date(endDate);

  const ITEMS_PER_PAGE = 100;
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);

  const booleanToYesNo = (v) => (v ? "yes" : "no");
  const yesNoToBoolean = (v) => v === "yes";

  const fetchUsers = async (currentPage = 1, perPage = ITEMS_PER_PAGE) => {
    try {
      const response = await axiosInstance.get(`/api/get_all_visitor`, {
        params: {
          page: Math.max(1, currentPage),
          per_page: perPage,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        },
      });

      if (response.status === 200) {
        setUsers(response.data.data);
        settotalRecords(response.data.Total_records || 0);
        setError(null);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("An error occurred while fetching users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, itemsPerPage); // ✅ use page and per-page correctly
  }, [page, itemsPerPage]);

  const handleVisitors = () => {
    navigate("/Visitor-Form"); // Navigate to the 'visitor' route
  };

  const handleSort = (key) => {
    // If the same column is clicked again, toggle the direction.
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedInvoices = [...users].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Special handling for date fields
      if (key === "visit_data") {
        aValue = new Date(a[key]);
        bValue = new Date(b[key]);
      }

      // Compare the values and return the sort order.
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    // Update the sort configuration and set the sorted invoices
    setSortConfig({ key, direction });
    setUsers(sortedInvoices); // Set sorted invoices
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const handleSearch = async (e, pageNumber = 1) => {
    const query = e.target.value; // Removed trim() to allow spaces
    setSearchQuery(query);
    setPage(pageNumber); // reset to 1

    if (!query.trim()) {
      // Check with trim() only for empty check
      return fetchUsers(1);
    }

    try {
      const response = await axiosInstance.get(`/api/search_visitor`, {
        params: {
          query,
          page: 1,
          per_page: ITEMS_PER_PAGE,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
        },
      });

      setUsers(response.data.data || []);
      settotalRecords(response.data.Total_records || 0);
      setError(response.data.data?.length ? null : "No users found");
    } catch (error) {
      console.error("Error fetching search results:", error);
      setError("An error occurred while fetching users");
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Debounce delay (adjust as needed)

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handlePaginationChange = (newPage, itemsPerPage) => {
    setPage(newPage);
    setItemsPerPage(itemsPerPage);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsSortPopup(false);
      }
    };

    // Only add the event listener when the popup is open
    if (isSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortPopup]);

  //   useEffect(() => {
  //   if (!isValidRange) return;         // don't fetch on invalid range
  //   setPage(1);
  //   fetchUsers(1);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [startDate, endDate]);

  const toggleSortPopup = (event) => {
    event.stopPropagation();
    setIsSortPopup(!isSortPopup);
  };

  const handleServiceChange = async (user, value) => {
    const newVal = value; // "yes" | "no"

    // Optimistic update
    const prevUsers = [...users];
    setUsers((cur) =>
      cur.map((u) =>
        u.id === user.id
          ? { ...u, services_availed: yesNoToBoolean(newVal) }
          : u
      )
    );

    setSavingIds((s) => new Set(s).add(user.id));

    try {
      const response = await axiosInstance.put(
        `/api/set_service_availed/${user.id}`,
        { service_avail: newVal }
      );
      // ✅ Explicitly sync with backend response
      const confirmed = response.data.service_avail; // "yes" or "no"
      setUsers((cur) =>
        cur.map((u) =>
          u.id === user.id
            ? { ...u, services_availed: confirmed === "yes" } // force boolean
            : u
        )
      );
      fetchUsers(page);
    } catch (err) {
      console.error("Failed to update service:", err);
      alert("Failed to update service. Reverting.");
      setUsers(prevUsers);
    } finally {
      setSavingIds((s) => {
        const ns = new Set(s);
        ns.delete(user.id);
        return ns;
      });
    }
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        !isRangeOpen ||
        rangePopupRef.current?.contains(e.target) ||
        rangeBtnRef.current?.contains(e.target)
      )
        return;
      setIsRangeOpen(false);
    };

    const onEsc = (e) => e.key === "Escape" && setIsRangeOpen(false);

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isRangeOpen]);

  return (
    <div
      className={`table-container visitor-management-page ${isSidebarExpanded ? "expanded" : "collapsed"
        }`}
    >
      <div className="table-container-content visitor-management-content">
        <div className="table-header">
          <div className="table-header-content">
            <div className="table-left-side">
              <h2 className="jost-bold">Visitors</h2>
            </div>
            <div className="table-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  className="search-bar"
                  value={searchQuery}
                  onChange={handleSearch} // Use handleSearch to update search query and fetch data
                />
                <IoIosSearch className="search-bar-icon" />
              </div>
              <div className="new-button" onClick={handleVisitors}>
                <span className="new-icon">
                  <LuPlus />
                </span>
                <span>New</span>
              </div>

              <button className="visitor-sort-button" onClick={toggleSortPopup}>
                <span className="action-icon custom-action-icon-size">
                  <PiSortAscendingThin />
                </span>
              </button>

              <div className="date-range-inline">
                <button
                  ref={rangeBtnRef}
                  className="btn btn-outline visitor-custom-date-button"
                  onClick={() => setIsRangeOpen((s) => !s)}
                  title="Choose a custom date range"
                  type="button"
                >
                  Custom Date
                  {/* {startDate || endDate ? (
                    <span className="date-chip">
                      {startDate || "—"} to {endDate || "—"}
                    </span>
                  ) : null} */}
                </button>

                {isRangeOpen && (
                  <div
                    ref={rangePopupRef}
                    className="custom-inline"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div className="date-range-header sub-title sort-popup-header">
                      Custom Date Range
                    </div>

                    <div className="date-range-fields">
                      <div>
                        <label className="label-sm">Start Date</label>
                        <input
                          type="date"
                          className="date-input p-2"
                          value={startDate}
                          onChange={(e) => {
                            let val = e.target.value;

                            // Ensure year is max 4 digits
                            if (val) {
                              const [y, m, d] = val.split("-");
                              if (y && y.length > 4) {
                                val = `${y.slice(0, 4)}-${m || "01"}-${d || "01"
                                  }`;
                              }
                            }

                            setStartDate(val);
                          }}
                          max={endDate || undefined}
                        />
                      </div>

                      <div>
                        <label className="label-sm">End Date</label>
                        <input
                          type="date"
                          className="date-input"
                          value={endDate}
                          onChange={(e) => {
                            let val = e.target.value;

                            // Ensure year is max 4 digits
                            if (val) {
                              const [y, m, d] = val.split("-");
                              if (y && y.length > 4) {
                                val = `${y.slice(0, 4)}-${m || "01"}-${d || "01"
                                  }`;
                              }
                            }

                            setEndDate(val);
                          }}
                          min={startDate || undefined}
                        />
                      </div>
                    </div>

                    <div className="date-range-actions">
                      <button
                        className="btn btn-primary custom-date-btn"
                        onClick={() => {
                          if (!isValidRange) {
                            alert("Start date must be before end date.");
                            return;
                          }
                          setPage(1);
                          fetchUsers(1);
                          setIsRangeOpen(false);
                        }}
                        type="button"
                        disabled={!isValidRange}
                      >
                        Apply
                      </button>

                      <button
                        className="btn btn-secondary btn-cancel"
                        onClick={() => {
                          setStartDate("");
                          setEndDate("");
                          setPage(1);
                          fetchUsers(1);
                          setIsRangeOpen(false);
                        }}
                        type="button"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isSortPopup && (
                <div className="sort-popup-container" ref={containerRef}>
                  <div className="sort-popup-header">
                    <span className="sort-title">Sort By</span>
                  </div>
                  <div className="sort-popup-content">
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("visitor_name")}
                    >
                      Visitor Name
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("visit_data")}
                    >
                      Visit Date
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("purpose_of_visit")}
                    >
                      Purpose Of Visit
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("employee_name")}
                    >
                      Employee To Meet
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body visitor-management-data-body">
          {loading ? (
            <p className="no-visitor">No Visitors Found</p> // Show loading text while fetching data
          ) : users.length === 0 ? ( // Check if users array is empty
            <p className="no-data-text">
              Currently, there are no records to display
            </p> // Show no data message when no records
          ) : (
            <>
              <div className="data-list-scroll-body visitor-management-scroll-body">
                {/* <div className="data-list-table"> */}
                  <table className="table multilevel-data visitor-management-table">
                  <thead className="sticky-header">
                    <tr className="table-row">
                      <th className="center-align">Sr. No</th>
                      <th><span style={{
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}> Name</span></th>
                      <th>Visit Date</th>
                      <th>Services Avail</th>
                      <th>Contact Number</th>
                      <th>Type Of Visitor</th>
                      <th><span style={{
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}>Purpose Of Visit</span></th>
                      <th><span style={{
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}>Employee To Meet</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => {
                      // define per-row state
                      const saving = savingIds.has(user.id);
                      const value = booleanToYesNo(user.services_availed);

                      return (
                        <tr key={user.id} className="table-row hoverable-row">
                          <td className="center-align">{user.serial_number}</td>
                          <td>
                            {user.visitor_name
                              ? user.visitor_name
                                .toLowerCase()
                                .split(' ')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                              : "N/A"}
                          </td>
                          <td className="center-align">
                            {new Date(user.visit_date).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </td>

                          <td>
                            <select
                              className="service-availed-select"
                              value={value}
                              onChange={(e) =>
                                handleServiceChange(user, e.target.value)
                              }
                              disabled={saving}
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                            {saving && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 12,
                                  color: "#888",
                                }}
                              >
                                Saving…
                              </span>
                            )}
                          </td>

                          <td className="center-align visitor-contact-cell">{user.contact_number}</td>
                          <td>
                            <div
                              className={`value-badge ${getVisitorTypeBadgeClass(
                                user.visit_type
                              )}`}
                            >
                              {formatBadgeLabel(user.visit_type)}
                            </div>
                          </td>

                          <td>
                            {user.purpose_of_visit
                              ? user.purpose_of_visit
                                .toLowerCase()
                                .split(' ')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                              : "N/A"}
                          </td>
                          <td>{user.employee_name
                            ? user.employee_name
                              .toLowerCase()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            : "N/A"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  </table>
                {/* </div> */}
              </div>
              <Pagination
                className="visitor-management-pagination"
                showPerPage={ITEMS_PER_PAGE}
                total={totalRecords}
                onPaginationChange={handlePaginationChange}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemUsers;
